import React from 'react';

import {createRoot} from 'react-dom/client';

import {AbstractFeature, allFeatures} from '#core/application/abstract-feature.js';

class WidgetsProviderServiceFeature extends AbstractFeature {
    constructor(parent) {
        super(parent);
        this.widgets = Object.create(null);
        this.allRoutes = [];
        this.mountedWidgetsByRegion = {};
    }

    get name() {
        return 'WidgetsProviderService';
    }

    updateMojitoConfig(mojitoConfig) {
        if (this.getWidgetData().noBaseUrl) {
            // if current URL is a widget, and has noBaseUrl flag
            mojitoConfig.core.baseUrl = '';
            mojitoConfig.services.transactionUrl = '';
        }

        super.updateMojitoConfig(mojitoConfig);
    }

    registerWidget(name, route, loader = null, data = {}) {
        const widget = {
            name: name,
            route: route,
            component: null,
            loader: loader,
            data: data,
        };

        this.widgets[name] = widget;

        if (route) {
            this.allRoutes.push(route);
        }
    }

    // Following is needed for 2-phase setup
    updateWidgetComponent(name, component) {
        const widget = this.widgets[name];
        widget.component = component;
        widget.loader = null;
    }

    resolveWidgetComponent(widget) {
        if (widget.component) {
            return Promise.resolve(widget.component);
        }

        if (!widget.loader) {
            return Promise.reject(new Error(`Widget "${widget.name}" is not registered with a component or loader`));
        }

        return widget.loader().then(Component => {
            if (!Component) {
                return Promise.reject(new Error(`Widget "${widget.name}" loader did not resolve a component`));
            }

            widget.component = Component;

            return Component;
        });
    }

    isFullscreenWidget() {
        return Boolean(this.getWidgetByCurrentRoute());
    }

    getWidgetData() {
        return this.getWidgetByCurrentRoute()?.data || {};
    }

    getWidgetByCurrentRoute() {
        const currentRoute = this.getCurrentRoute();
        const allWidgets = Object.values(this.widgets);

        return allWidgets.find(widget => currentRoute.startsWith(widget.route));
    }

    getCurrentRoute() {
        const {pathname} = window.location;

        if (!pathname) {
            return '';
        }

        // Remove first and last slash with all parameters
        return pathname.replace(/^\/|\/$|\?.*$|\/\?.*$/g, '');
    }

    mountWidget(widgetId, regionId, params = {}) {
        const widgetName = widgetId;
        const region = document.getElementById(regionId);
        if (!region) {
            const msg = `RegionId "${regionId}" does not point to a DOM element`;
            this.logger.error(msg);

            return Promise.reject(new Error(msg));
        }
        if (!this.widgets[widgetName]) {
            const msg = `Widget "${widgetName}" is not registered`;
            this.logger.error(msg);

            return Promise.reject(new Error(msg));
        }
        if (this.mountedWidgetsByRegion[regionId]) {
            const msg = `RegionId "${regionId}" is already occupied by widget "${this.mountedWidgetsByRegion[regionId].widgetId}"`;
            this.logger.error(msg);

            return Promise.reject(new Error(msg));
        }

        this.mountedWidgetsByRegion[regionId] = {
            widgetId,
            pending: true,
        };

        return this.resolveWidgetComponent(this.widgets[widgetName])
            .then(Component => {
                if (!this.mountedWidgetsByRegion[regionId]?.pending) {
                    return;
                }

                const root = createRoot(region, {
                    onRecoverableError(error) {
                        const message = String(error?.message || error);

                        // TODO: investigate this error separately
                        if (message.includes('There was an error during concurrent rendering')) return;
                    },
                });

                root.render(React.createElement(Component, {params}));

                this.mountedWidgetsByRegion[regionId] = {
                    root,
                    widgetId, // not used at the moment
                };
            })
            .catch(e => {
                if (this.mountedWidgetsByRegion[regionId]?.pending) {
                    delete this.mountedWidgetsByRegion[regionId];
                }

                const msg = `Failed to mount widget "${widgetName}" at regionId "${regionId}"`;
                this.logger.error(msg, e);

                return Promise.reject(new Error(msg));
            });
    }

    unmountWidget(regionId) {
        const entry = this.mountedWidgetsByRegion[regionId];

        if (!entry) {
            this.logger.error(`Widget with RegionId "${regionId}" is not a part of mounted widgets, failed to unmount`);

            return false;
        }

        if (entry.pending) {
            delete this.mountedWidgetsByRegion[regionId];

            return true;
        }

        try {
            entry.root.unmount();
            delete this.mountedWidgetsByRegion[regionId];
        } catch (e) {
            this.logger.error(`Failed to unmount widget at: ${regionId}`);

            return false;
        }

        return true;
    }

    unmountAllWidgets() {
        let result = true;
        Object.keys(this.mountedWidgetsByRegion).forEach(regionId => {
            const res = this.unmountWidget(regionId);
            result = result & res;
        });
        this.mountedWidgetsByRegion = {};

        return result;
    }
}

export const WidgetsProviderService = new WidgetsProviderServiceFeature(allFeatures);
